import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { useApi } from '../services/api';
import { useAnalytics } from '../hooks/useAnalytics';

// Import after exporting USER_STATES to avoid circular dependency
// import { STATE_FEATURES } from '../config/protectedContent';


// Define user states
export const USER_STATES = {
  BROWSING: 'browsing',
  TRIAL: 'trial_user',
  PREMIUM: 'premium_user',
  EXPIRED: 'expired_user'
};

// Legacy mapping for cached localStorage states from before free tier removal
const LEGACY_STATE_MAP = {
  'free_user': null  // Will be resolved via API re-fetch
};

// Define feature permissions per state
const ALL_PREMIUM_FEATURES = [
  'view_basic_odds',
  'view_model_predictions',
  'view_ev_data',
  'view_historical_data',
  'export_data'
];

export const STATE_FEATURES = {
  [USER_STATES.BROWSING]: [
    'view_basic_odds'
  ],
  [USER_STATES.TRIAL]: ALL_PREMIUM_FEATURES,
  [USER_STATES.PREMIUM]: ALL_PREMIUM_FEATURES,
  [USER_STATES.EXPIRED]: [
    'view_basic_odds'
  ]
};

const UserContext = createContext();

export const UserProvider = ({ children }) => {
  // Global bookmaker preference — persisted to localStorage
  const [bookmakerPreference, setBookmakerPreferenceState] = useState(() => {
    try {
      // Migrate from old dashboard-only key if global key not yet set
      const global = localStorage.getItem('bookmaker-preference');
      if (global) return global;
      const legacy = localStorage.getItem('dashboard-bookmaker-preference');
      if (legacy) {
        localStorage.setItem('bookmaker-preference', legacy);
        localStorage.removeItem('dashboard-bookmaker-preference');
        return legacy;
      }
      return 'both';
    } catch {
      return 'both';
    }
  });

  // Onboarding answers (#184). Saved to Auth0 user_metadata through
  // /api/users/me/preferences so the briefing email (#196) can filter by
  // book; localStorage is only the fast local copy. `onboarded` is null until
  // the server has answered, so nothing redirects on a guess. `briefingOptIn`
  // is the daily briefing email (#196); null until the server has answered.
  // `briefingAvailable` is whether that email is sending at all -- the opt-in
  // is offered only when it is.
  const [preferences, setPreferences] = useState(() => {
    let unitSize = null;
    try {
      const stored = Number(localStorage.getItem('pev-unit-size'));
      unitSize = Number.isFinite(stored) && stored > 0 ? stored : null;
    } catch {
      // localStorage unavailable
    }
    return { unitSize, onboarded: null, briefingOptIn: null, briefingAvailable: false };
  });

  const writeLocalBook = (value) => {
    setBookmakerPreferenceState(value);
    try {
      localStorage.setItem('bookmaker-preference', value);
    } catch {
      // localStorage unavailable
    }
  };

  // Initialize state from localStorage if available, but validate it
  const [userState, setUserState] = useState(() => {
    try {
      const cached = localStorage.getItem('user_state');
      if (cached) {
        const parsedState = JSON.parse(cached);
        // console.log('Found cached state:', parsedState);
        
        // If we have a valid cached state with user data, use it for immediate render hints
        // (e.g., showing user name) but keep isLoading: true until Auth0 confirms the session.
        // This prevents the app from briefly rendering authenticated UI then redirecting away
        // if the Auth0 session has expired since the cache was written.
        if (parsedState.user && parsedState.status) {
          // Handle legacy 'free_user' cached state — force re-fetch from API
          if (LEGACY_STATE_MAP.hasOwnProperty(parsedState.status)) {
            localStorage.removeItem('user_state');
            return {
              status: USER_STATES.BROWSING,
              isLoading: true,
              user: parsedState.user,  // Keep user hint for render
              subscription: null,
              error: null,
              previousStatus: null
            };
          }
          return {
            ...parsedState,
            isLoading: true,  // Auth0 must confirm before granting access
            authCallbackInProgress: false  // Reset transient flags from previous session
          };
        }
      }
    } catch (error) {
      console.error('Error parsing cached state:', error);
    }
    
    // Default initial state if no valid cache
    // console.log('Using default browsing state');
    return {
      status: USER_STATES.BROWSING,
      isLoading: false,  // Don't show loading initially
      user: null,
      subscription: null,
      error: null,
      previousStatus: null
    };
  });

  const { isAuthenticated, isLoading: authLoading, getAccessTokenSilently, user, logout } = useAuth0();
  const { callApi } = useApi();

  /**
   * Save any of { bookmakerPreference, unitSize, onboarded, briefingOptIn }.
   * Local state moves first; the server copy follows and failures are logged,
   * not thrown -- a failed save means the question is asked again, never a
   * lost edge. Resolves true once the server has it. The briefing opt-in is
   * rolled back on failure, because a toggle that reads "on" while the server
   * says off promises an email that will never arrive.
   */
  const savePreferences = useCallback(async ({ bookmakerPreference: book, unitSize, onboarded, briefingOptIn } = {}) => {
    const body = {};
    if (book !== undefined) {
      writeLocalBook(book);
      body.bookmaker_preference = book;
    }
    if (unitSize !== undefined) {
      setPreferences(prev => ({ ...prev, unitSize }));
      try {
        if (unitSize) localStorage.setItem('pev-unit-size', String(unitSize));
        else localStorage.removeItem('pev-unit-size');
      } catch {
        // localStorage unavailable
      }
      body.unit_size = unitSize;
    }
    if (onboarded) {
      setPreferences(prev => ({ ...prev, onboarded: true }));
      body.onboarded = true;
    }
    if (briefingOptIn !== undefined) {
      setPreferences(prev => ({ ...prev, briefingOptIn }));
      body.briefing_opt_in = briefingOptIn;
    }
    if (!isAuthenticated || Object.keys(body).length === 0) return false;
    try {
      await callApi('users/me/preferences', { method: 'PATCH', body });
      return true;
    } catch (err) {
      console.error('Saving preferences failed:', err);
      if (briefingOptIn !== undefined) {
        // It is a toggle, so the value before this save was the opposite one.
        setPreferences(prev => ({ ...prev, briefingOptIn: !briefingOptIn }));
      }
      return false;
    }
  }, [callApi, isAuthenticated]);

  // Book toggles are clicky; save the last one once the clicking stops
  // rather than one Management API write per click, out of order.
  const bookSaveTimer = useRef(null);
  const setBookmakerPreference = (value) => {
    writeLocalBook(value);
    if (!isAuthenticated) return;
    clearTimeout(bookSaveTimer.current);
    bookSaveTimer.current = setTimeout(() => {
      callApi('users/me/preferences', { method: 'PATCH', body: { bookmaker_preference: value } })
        .catch(err => console.error('Saving book preference failed:', err));
    }, 800);
  };

  // Server preferences are applied once per signed-in user. users/me is read
  // again on token refresh, and callApi can hand back a cached pre-onboarding
  // body -- re-applying that would undo onboarding mid-session.
  const hydratedForRef = useRef(null);
  const { trackEvent, trackTierStatusIdentified } = useAnalytics();

  // console.log('UserProvider render:', {
  //   isAuthenticated,
  //   authLoading,
  //   status: userState.status || USER_STATES.BROWSING,  // Provide fallback
  //   isLoading: userState.isLoading
  // });

  const [sessionWarningShown, setSessionWarningShown] = useState(false);
  // Use ref for authCallbackInProgress — refs update synchronously and prevent
  // stale closure issues that cause duplicate auth callback requests
  const authCallbackInProgressRef = useRef(false);

  // Helper function to update state and persist to localStorage
  const updateUserState = useCallback((newState) => {
    setUserState(prevState => {
      const updatedState = {
        ...prevState,
        ...newState,
        // Only update previousStatus if status is explicitly being changed (not undefined)
        previousStatus: (newState.status !== undefined && newState.status !== prevState.status)
          ? prevState.status
          : prevState.previousStatus
      };

      // Persist to localStorage unless explicitly clearing to BROWSING
      // Use updatedState.status (not newState.status) to handle partial updates correctly
      if (updatedState.status !== USER_STATES.BROWSING || updatedState.user) {
        localStorage.setItem('user_state', JSON.stringify(updatedState));
      }

      // Track user state changes for analytics - only when status actually changes
      // Check that newState.status is defined to avoid spurious events from partial updates
      if (newState.status !== undefined && newState.status !== prevState.status) {
        trackEvent('user_state_changed', {
          category: 'Authentication',
          previous_state: prevState.status,
          new_state: updatedState.status,
          is_authenticated: !!updatedState.user
        });

        // Track tier status identification
        trackTierStatusIdentified(
          updatedState.status || USER_STATES.BROWSING,
          prevState.status || USER_STATES.BROWSING,
          {
            source: 'user_context',
            has_user: !!updatedState.user
          }
        );
      }

      return updatedState;
    });
  }, [trackEvent, trackTierStatusIdentified]);

  useEffect(() => {
    // console.log('Auth status changed:', { 
    //   isAuthenticated, 
    //   authLoading, 
    //   status: userState.status || USER_STATES.BROWSING
    // });
  }, [isAuthenticated, authLoading, userState.status]);

  useEffect(() => {
    let isMounted = true;

    const determineUserState = async () => {
      console.log('=== User State Change ===', {
        isAuthenticated,
        email: user?.email,
        emailVerified: user?.email_verified,
        signupMethod: user?.identities?.[0]?.provider
      });

      if (authLoading) return;

      if (!isAuthenticated) {
        if (isMounted) {
          updateUserState({
            status: USER_STATES.BROWSING,
            isLoading: false,
            user: null,
            subscription: null,
            error: null
          });
        }
        return;
      }

      try {
        if (user) {
          // Set loading state before making API call (Bug 1 fix)
          if (isMounted) {
            updateUserState({
              isLoading: true
            });
          }
          
          // Fetch premium status from API (checks app_metadata.role)
          // This is more reliable than JWT token roles
          try {
            const userData = await callApi('users/me');
            const isPremium = userData?.isPremium || false;
            const hasUsedTrial = userData?.has_used_trial || false;
            const subscriptionStatus = userData?.subscription?.status;

            // Determine tier: TRIAL (trialing sub), PREMIUM (active sub), EXPIRED (used trial, no sub), BROWSING (new user)
            let initialStatus;
            if (isPremium && subscriptionStatus === 'trialing') {
              initialStatus = USER_STATES.TRIAL;
            } else if (isPremium) {
              initialStatus = USER_STATES.PREMIUM;
            } else if (hasUsedTrial) {
              initialStatus = USER_STATES.EXPIRED;
            } else {
              // New authenticated user without trial — gets BROWSING-level access (restricted).
              // Login.js always redirects new signups to /dashboard?showUpgrade=true which
              // auto-opens the trial dialog. ProtectedColumn checks Auth0 isAuthenticated
              // separately, so authenticated BROWSING users see upgrade dialog (not login).
              initialStatus = USER_STATES.BROWSING;
            }

            // Fallback to JWT roles if API fails
            const jwtRoles = user['https://auth.plusevbets.com/roles'] || [];
            const fallbackStatus = jwtRoles.includes('premium_user') ? USER_STATES.PREMIUM : USER_STATES.BROWSING;
            const finalStatus = userData ? initialStatus : fallbackStatus;
            
            // Always update state from API response - API is source of truth
            // This ensures premium status syncs even if cached state is stale
            if (isMounted) {
              updateUserState({
                status: finalStatus,
                roles: jwtRoles,
                has_used_trial: hasUsedTrial,
                user: {
                  id: user.sub,
                  email: user.email,
                  name: user.name,
                  roles: jwtRoles,
                  created_at: userData?.created_at || user.created_at
                },
                subscription: userData?.subscription || null,
                isLoading: false
              });

              const serverPrefs = userData?.preferences;
              if (serverPrefs && hydratedForRef.current !== user.sub) {
                hydratedForRef.current = user.sub;
                // Local copies belong to whoever last signed in on this
                // browser; never hand one user's book or unit to another.
                let owner = null;
                try { owner = localStorage.getItem('pev-prefs-owner'); } catch { /* unavailable */ }
                const localIsMine = !owner || owner === user.sub;
                if (serverPrefs.bookmaker_preference) {
                  writeLocalBook(serverPrefs.bookmaker_preference);
                } else if (localIsMine) {
                  // A choice made on the Account page before preferences were
                  // stored server-side lives only in this browser. Carry it up
                  // once, so the briefing email does not assume "both".
                  let local = null;
                  try { local = localStorage.getItem('bookmaker-preference'); } catch { /* unavailable */ }
                  if (local && local !== 'both') {
                    callApi('users/me/preferences', { method: 'PATCH', body: { bookmaker_preference: local } })
                      .catch(err => console.error('Migrating book preference failed:', err));
                  }
                } else {
                  writeLocalBook('both');
                }
                if (!localIsMine) {
                  try { localStorage.removeItem('pev-unit-size'); } catch { /* unavailable */ }
                }
                try { localStorage.setItem('pev-prefs-owner', user.sub); } catch { /* unavailable */ }
                setPreferences(prev => ({
                  unitSize: serverPrefs.unit_size ?? (localIsMine ? prev.unitSize : null),
                  onboarded: Boolean(serverPrefs.onboarded),
                  briefingOptIn: Boolean(serverPrefs.briefing_opt_in),
                  briefingAvailable: Boolean(serverPrefs.briefing_available),
                }));
              }
            }
          } catch (apiError) {
            console.error('Error fetching user data from API:', apiError);

            // Only force re-auth on actual 401 from the backend (invalid/expired JWT)
            // Token acquisition errors (isTokenError) should NOT trigger logout —
            // the SDK couldn't get a token, but the user's session may still be valid
            const isAuthError = apiError.status === 401 && !apiError.isTokenError;

            if (isAuthError) {
              console.log('Auth error detected during user state determination - forcing re-authentication');
              localStorage.removeItem('user_state');
              if (isMounted) {
                updateUserState({
                  status: USER_STATES.BROWSING,
                  isLoading: false,
                  user: null,
                  subscription: null,
                  error: 'Your session has expired. Please log in again.'
                });
              }
              // Trigger immediate logout to clear Auth0 session
              logout({ logoutParams: { returnTo: window.location.origin, federated: true } });
              return;
            }

            // For non-auth errors, fallback to JWT roles
            const roles = user['https://auth.plusevbets.com/roles'] || [];
            const initialStatus = roles.includes('premium_user') ? USER_STATES.PREMIUM : USER_STATES.BROWSING;

            // Always update state - avoids stale closure issues with conditional checks
            if (isMounted) {
              updateUserState({
                status: initialStatus,
                roles: roles,
                user: {
                  id: user.sub,
                  email: user.email,
                  name: user.name,
                  roles: roles
                },
                isLoading: false
              });
            }
          }
        }
      } catch (error) {
        console.error('UserContext error:', error);
        // Ensure isLoading is reset on error
        if (isMounted) {
          updateUserState({
            isLoading: false
          });
        }
      }
    };

    determineUserState();

    return () => { isMounted = false; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, authLoading, user, logout, callApi, updateUserState]);

  // Periodic token refresh — skip initial check to avoid racing with login flow
  useEffect(() => {
    if (isAuthenticated) {
      const checkSession = async () => {
        try {
          await getAccessTokenSilently({
            detailedResponse: true,
            timeoutInSeconds: 60
          });

          if (sessionWarningShown) {
            setSessionWarningShown(false);
            trackEvent('token_refresh_success', {
              category: 'Authentication',
              after_warning: true
            });
          }

        } catch (error) {
          console.error('Session check failed:', error);

          if (error.error === 'login_required' ||
              error.error === 'consent_required') {

            console.log('Session expired. Logging out user...');

            trackEvent('session_expired', {
              category: 'Authentication',
              error_type: error.error,
              error_message: error.message
            });

            localStorage.removeItem('user_state');
            updateUserState({
              status: USER_STATES.BROWSING,
              isLoading: false,
              user: null,
              subscription: null,
              error: 'Your session has expired. Please log in again.'
            });

            logout({ logoutParams: { returnTo: window.location.origin, federated: true } });
          }
          // For "Missing Refresh Token" errors, don't force logout here —
          // the useRefreshTokensFallback option will try iframe auth,
          // and determineUserState handles actual API failures
        }
      };

      // Check session every 10 minutes — skip initial check to avoid racing with login
      const sessionCheck = setInterval(checkSession, 600000);

      return () => clearInterval(sessionCheck);
    }
  }, [isAuthenticated, getAccessTokenSilently, sessionWarningShown, logout, trackEvent, updateUserState]);

  // Auth callback handler — notify backend of login for email service tracking
  useEffect(() => {
    const handleUserAuth = async () => {
      // Use ref for synchronous guard — prevents duplicate calls from React 18 double-invocation
      if (authCallbackInProgressRef.current) {
        console.log('Auth callback already in progress, skipping');
        return;
      }

      // Skip if we already have this user's data
      if (
        userState.user?.id === user.sub &&
        (userState.status === USER_STATES.TRIAL || userState.status === USER_STATES.PREMIUM || userState.status === USER_STATES.EXPIRED)
      ) {
        console.log('Skipping auth callback - user already initialized');
        return;
      }

      authCallbackInProgressRef.current = true;

      try {
        const userData = {
          id: user.sub,
          email: user.email,
          name: user.name,
          given_name: user.email,
          family_name: ''
        };

        console.log('Sending auth callback with user data:', userData);
        const response = await callApi('auth/callback', {
          method: 'POST',
          body: userData
        });

        console.log('Auth callback successful:', response);

        if (response?.status === 'success') {
          // Only update user info here, not status
          // Status is determined by the /api/users/me effect to avoid race conditions
          updateUserState({
            user: {
              ...userData,
              roles: user['https://auth.plusevbets.com/roles'] || []
            }
          });
        }
      } catch (error) {
        // Auth callback errors are non-fatal — it's for email tracking, not auth
        console.error('Auth callback error (non-fatal):', error);
      } finally {
        authCallbackInProgressRef.current = false;
      }
    };

    if (user && (!userState.user || user.sub !== userState.user?.id)) {
      handleUserAuth();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, user, userState.user, userState.status, callApi, updateUserState]);

  // Compute trial days remaining from subscription data
  const trialDaysRemaining = (() => {
    if (userState.status !== USER_STATES.TRIAL || !userState.subscription?.trial_end) return null;
    const trialEnd = new Date(userState.subscription.trial_end * 1000);
    const now = new Date();
    return Math.max(0, Math.ceil((trialEnd - now) / (1000 * 60 * 60 * 24)));
  })();

  const value = {
    ...userState,
    isLoading: userState.isLoading,
    trialDaysRemaining,
    bookmakerPreference,
    setBookmakerPreference,
    preferences,
    savePreferences,
    isBrowsing: () => (userState.status || USER_STATES.BROWSING) === USER_STATES.BROWSING,
    isTrial: () => userState.status === USER_STATES.TRIAL,
    isPremium: () => userState.status === USER_STATES.PREMIUM || userState.status === USER_STATES.TRIAL,
    isExpired: () => userState.status === USER_STATES.EXPIRED,
    // Returns true if user has full feature access (trial or premium)
    hasFullAccess: () => userState.status === USER_STATES.TRIAL || userState.status === USER_STATES.PREMIUM,
    hasFeatureAccess: (feature) => {
      const currentStatus = userState.status || USER_STATES.BROWSING;
      const currentFeatures = STATE_FEATURES[currentStatus];
      return currentFeatures ? currentFeatures.includes(feature) : false;
    },
  };

  return (
    <UserContext.Provider value={value}>
      {children}
    </UserContext.Provider>
  );
};

export const useUser = () => useContext(UserContext); 